import {
  IAutoOrderFirestoreOrder,
  IAutoOrderFirestoreOrderErrorMessage,
  IAutoOrderFirestoreOrderItem,
  IAutoOrderFirestoreOrderPreparedInfo,
  IAutoOrderFirestoreOrderPreparedInfoDetails,
  IAutoOrderFirestoreOrderSettings,
} from './auto-order-firestore-order';
import {DEFAULT_STORE, IStore} from '../../../shared/shared-models/store/store';
import {IAutoOrderOrderStockItem} from './auto-order-order-stock-item';
import {ISupplier} from '../../../shared/shared-models/stock/suppliers';

//#region INTERFACE IAutoOrderOrder

/**
 * ### IAutoOrderOrder
 * Extended auto order interface including additional metadata and supplier linkage.
 *
 * @interface IAutoOrderOrder
 * @extends IAutoOrderFirestoreOrder
 * @extends Object
 * @description This interface extends the base `IAutoOrderFirestoreOrder` by adding properties
 *              related to sequence, supplier linkage, hold status, order totals, recipients, and supplier details.
 *
 * @example
 * const example: IAutoOrderOrder = {
 *   orderId: "ORDER123",
 *   emailStatus: "SENT",
 *   iqStatus: "SUCCESS",
 *   orderItems: [],
 *   orderSettings: { growth: 5, monthsHistory: 3, period: 14, stockDays: 7 },
 *   owned: "org_456",
 *   pdfStatus: "READY",
 *   scheduled: "SCHEDULED",
 *   status: "CREATED",
 *   supplierId: "SUP789",
 *   userId: "USER123",
 *   createdSequence: 42,
 *   linkedToSupplierId: "SUP456",
 *   linkedToSupplierName: "Supplier Inc.",
 *   onHold: ["item001", "item002"],
 *   orderTotal: 1234.56,
 *   recipients: ["user@example.com"],
 *   supplierName: "Supplier Inc.",
 *   supplierMinOrder: 100
 * };
 */
export interface IAutoOrderOrder extends IAutoOrderFirestoreOrder, Object {
  /**
   * Sequence number assigned when the order was created.
   * @type {number}
   * @optional
   * @example 42
   */
  createdSequence?: number;

  /**
   * Identifier of the linked supplier, if any.
   * @type {string}
   * @optional
   * @example "SUP456"
   */
  linkedToSupplierId?: string;

  /**
   * Name of the linked supplier.
   * @type {string}
   * @optional
   * @example "Supplier Inc."
   */
  linkedToSupplierName?: string;

  /**
   * Array of item codes currently on hold for the order.
   * @type {string[]}
   * @optional
   * @example ["item001", "item002"]
   */
  onHold?: string[];

  /**
   * Total monetary value of the order.
   * @type {number}
   * @optional
   * @example 1234.56
   */
  orderTotal?: number;

  /**
   * List of recipient email addresses for the order notifications.
   * @type {string[]}
   * @optional
   * @example ["user@example.com"]
   */
  recipients?: string[];

  /**
   * Minimum order value required by the supplier.
   * @type {number}
   * @optional
   * @example 100
   */
  supplierMinOrder?: number;

  /**
   * Name of the supplier for this order.
   * @type {string}
   * @optional
   * @example "Supplier Inc."
   */
  supplierName?: string;
}

//#endregion

//#region INTERFACE IAutoOrderOrderSettings

/**
 * ### IAutoOrderOrderSettings
 * Extends Firestore order settings with base object type.
 *
 * @interface IAutoOrderOrderSettings
 * @extends IAutoOrderFirestoreOrderSettings
 * @extends Object
 * @description This interface extends the Firestore order settings interface
 *              to ensure it also behaves as a general object type.
 *              No additional properties are introduced.
 *
 * @example
 * const settings: IAutoOrderOrderSettings = {
 *   growth: 10,
 *   monthsHistory: 6,
 *   period: 14,
 *   stockDays: 7
 * };
 */
export interface IAutoOrderOrderSettings extends IAutoOrderFirestoreOrderSettings, Object {
}

//#endregion

//#region INTERFACE IAutoOrderOrderPropertyPayload
/**
 * ### IAutoOrderOrderPropertyPayload
 * Generic payload type used to set a specific property on an auto-generated order.
 *
 * @interface IAutoOrderOrderPropertyPayload
 * @extends Object
 * @template Key extends keyof IAutoOrderOrder
 * @description Represents a strongly typed payload for updating a single property on an `IAutoOrderOrder`.
 *              The generic type `Key` ensures that the `field` is a valid property key and `value` is correctly typed.
 *              This enables precise and safe updates to a dynamic field of an order.
 *
 * @example
 * const payload: IAutoOrderOrderPropertyPayload<'status'> = {
 *   orderId: 'order-123',
 *   field: 'status',
 *   value: 'PENDING'
 * };
 */
export interface IAutoOrderOrderPropertyPayload<Key extends keyof IAutoOrderOrder> {
  /**
   * The property key of the auto order to update.
   * @description Must be a valid key of the `IAutoOrderOrder` interface.
   *
   * @type {Key}
   * @example "status"
   */
  field: Key;

  /**
   * Unique identifier for the target auto order.
   * @description Specifies which auto-generated order this update applies to.
   *
   * @type {string}
   * @example "order-123"
   */
  orderId: string;

  /**
   * The new value to assign to the specified field.
   * @description Must match the type of the corresponding key in `IAutoOrderOrder`.
   *
   * @type {IAutoOrderOrder[Key]}
   * @example "PENDING"
   */
  value: IAutoOrderOrder[Key];
}

//#endregion


//#region INTERFACE IAutoOrderOrderItem

/**
 * ### IAutoOrderOrderItem
 * Extends Firestore order item properties with base object type.
 *
 * @interface IAutoOrderOrderItem
 * @extends IAutoOrderFirestoreOrderItem
 * @description This interface extends the Firestore order item interface to
 *              maintain consistency with object typing without adding new properties.
 *
 * @example
 * const item: IAutoOrderOrderItem = {
 *   code: "ITEM123",
 *   netSold: 25
 * };
 */
export type IAutoOrderOrderItem = IAutoOrderFirestoreOrderItem

//#endregion

//#region CONST DEFAULT_AUTO_ORDER_ORDER_ITEM

/**
 * ### DEFAULT_AUTO_ORDER_ORDER_ITEM
 * Default initialized object for `IAutoOrderOrderItem` representing a single order line.
 *
 * @constant DEFAULT_AUTO_ORDER_ORDER_ITEM
 * @type {IAutoOrderOrderItem}
 * @defaults {@link IAutoOrderOrderItem}
 * @description
 * This constant provides the base structure for an auto-order item entry,
 * ensuring that all required fields are safely initialized with default values.
 * It is used during the creation of a new auto-generated order or when resetting
 * individual order item lines to a clean state.
 *
 * @example
 * const defaultItem: IAutoOrderOrderItem = { ...DEFAULT_AUTO_ORDER_ORDER_ITEM };
 */
export const DEFAULT_AUTO_ORDER_ORDER_ITEM: IAutoOrderOrderItem = {
  /**
   * Primary supplier ID for the item.
   * Typically defaults to `1` for main supplier unless overridden.
   * @type {number}
   * @default 1
   * @example mainSupplier: 1
   */
  mainSupplier: 1,

  /**
   * Maximum number of units returned for the item in the given period.
   * @type {number}
   * @default 0
   * @example maxReturned: 0
   */
  maxReturned: 0,

  /**
   * Maximum number of units sold in the given period.
   * @type {number}
   * @default 0
   * @example maxSold: 0
   */
  maxSold: 0,

  /**
   * Status of the item when it is first introduced into the order.
   * Always defaults to `'ADDED'` to indicate a new line.
   * @type {'ADDED'}
   * @default 'ADDED'
   * @example new: 'ADDED'
   */
  new: 'ADDED',

  /**
   * Number of days the item recorded negative stock.
   * @type {number}
   * @default 0
   * @example negativeDays: 0
   */
  negativeDays: 0,

  /**
   * Net quantity sold for the item (e.g., after returns).
   * @type {number}
   * @default 0
   * @example netSold: 0
   */
  netSold: 0,

  /**
   * Number of days the item was completely out of stock.
   * @type {number}
   * @default 0
   * @example outOfStock: 0
   */
  outOfStock: 0,

  /**
   * Difference in quantity used for adjusting ordering decisions.
   * @type {number}
   * @default 0
   * @example qtyDiff: 0
   */
  qtyDiff: 0,

  /**
   * Number of days the item recorded zero sales.
   * @type {number}
   * @default 0
   * @example zeroDays: 0
   */
  zeroDays: 0,
};

//#endregion


//#region INTERFACE IAutoOrderOrderErrorMessage

/**
 * ### IAutoOrderOrderErrorMessage
 * Extends Firestore order error message with base object type.
 *
 * @interface IAutoOrderOrderErrorMessage
 * @extends IAutoOrderFirestoreOrderErrorMessage
 * @extends Object
 * @description This interface extends the Firestore order error message interface
 *              to maintain consistent object typing without additional properties.
 *
 * @example
 * const errorMsg: IAutoOrderOrderErrorMessage = {
 *   code: "ERR001",
 *   message: "An error occurred"
 * };
 */
export interface IAutoOrderOrderErrorMessage extends IAutoOrderFirestoreOrderErrorMessage, Object {
}

//#endregion

//#region INTERFACE IAutoOrderOrderPreparedInfo

/**
 * ### IAutoOrderOrderPreparedInfo
 * Extends Firestore prepared info for orders with base object type.
 *
 * @interface IAutoOrderOrderPreparedInfo
 * @extends IAutoOrderFirestoreOrderPreparedInfo
 * @extends Object
 * @description This interface extends the Firestore prepared info interface for
 *              orders, ensuring it behaves as a generic object type.
 *
 * @example
 * const preparedInfo: IAutoOrderOrderPreparedInfo = {
 *   preparedItems: {},
 *   total: 100,
 *   ts: new Date()
 * };
 */
export interface IAutoOrderOrderPreparedInfo extends IAutoOrderFirestoreOrderPreparedInfo, Object {
}

//#endregion

//#region INTERFACE IAutoOrderOrderPreparedInfoDetails

/**
 * ### IAutoOrderOrderPreparedInfoDetails
 * Extends Firestore prepared info details with base object type.
 *
 * @interface IAutoOrderOrderPreparedInfoDetails
 * @extends IAutoOrderFirestoreOrderPreparedInfoDetails
 * @extends Object
 * @description This interface extends the Firestore prepared info details interface,
 *              maintaining consistent object typing without extra properties.
 *
 * @example
 * const detail: IAutoOrderOrderPreparedInfoDetails = {
 *   qty: 10,
 *   desc: "Sample description",
 *   price: 15.5
 * };
 */
export interface IAutoOrderOrderPreparedInfoDetails extends IAutoOrderFirestoreOrderPreparedInfoDetails, Object {
}

//#endregion


//#region INTERFACE IAutoOrderOrderProgressStep

/**
 * ### IAutoOrderOrderProgressStep
 * Tracks the boolean progress state of each step involved in generating or opening an auto order.
 *
 * @interface IAutoOrderOrderProgressStep
 * @extends Object
 * @defaults {@link DEFAULT_AUTO_ORDER_PROGRESS_STEP}
 * @description Represents the status flags for each step in the old-models process.
 *              This structure is primarily used to show progress indicators (e.g., stepper or loader) in the UI.
 *              Each boolean property indicates whether the corresponding step has been completed during the workflow.
 *
 * @example
 * const progress: IAutoOrderOrderProgressStep = {
 *   openAutoOrderOrderStarted: true,
 *   getSelectedStore: true,
 *   setSelectedOrder: false,
 *   ...
 * };
 */
export interface IAutoOrderOrderProgressStep extends Object {
  /**
   * Confirms that stock item arrays have been concatenated into a unified list.
   * @type {boolean}
   * @example true
   */
  concatenateStockItems: boolean;

  /**
   * Signals that duplicate entries in the stock list have been identified and removed.
   * @type {boolean}
   * @example false
   */
  deDuplicateStockItems: boolean;

  /**
   * Confirms that order items missing necessary codes have been identified.
   * @type {boolean}
   * @example false
   */
  determineMissingOrderItemCodes: boolean;

  /**
   * Indicates that the process to check for missing stock codes has been completed.
   * @type {boolean}
   * @example true
   */
  determineMissingStockCodes: boolean;

  /**
   * Indicates whether any previously edited stock items have been loaded from local storage.
   * @type {boolean}
   * @example true
   */
  getEditedStockItemsFromLocalStorage: boolean;

  /**
   * Denotes whether the existing order items have been loaded from Firestore or backend.
   * @type {boolean}
   * @example true
   */
  getOrderItems: boolean;

  getOrderItemsAdded: boolean;

  /**
   * Marks whether the store context has been retrieved from state or Firestore.
   * @type {boolean}
   * @example true
   */
  getSelectedStore: boolean;

  /**
   * Indicates successful retrieval of stock items from any additional suppliers.
   * @type {boolean}
   * @example false
   */
  getStockItemsAdditionalSupplier: boolean;

  /**
   * Indicates successful retrieval of stock items from the regular supplier.
   * @type {boolean}
   * @example true
   */
  getStockItemsRegularSupplier: boolean;

  /**
   * Indicates that all necessary steps to open or prepare the auto order have been completed.
   * @type {boolean}
   * @example true
   */
  openAutoOrderOrderCompleted: boolean;


  /**
   * Indicates that the auto order workflow has been initiated.
   * @type {boolean}
   * @example true
   */
  openAutoOrderOrderStarted: boolean;

  /**
   * Signals that the currently selected order has been committed to state.
   * @type {boolean}
   * @example false
   */
  setSelectedOrder: boolean;

  setSelectedSupplier: boolean;
}

//#endregion

//#region CONST DEFAULT_AUTO_ORDER_PROGRESS_STEP

/**
 * ### DEFAULT_AUTO_ORDER_PROGRESS_STEP
 * Default initialization state for all steps involved in the auto order workflow.
 *
 * @const DEFAULT_AUTO_ORDER_PROGRESS_STEP
 * @type {IAutoOrderOrderProgressStep}
 * @description Represents the initial (all `false`) state of the step tracker.
 *              Useful for initializing the store or resetting order progress at the beginning of the workflow.
 *
 * @example
 * const progressSteps = { ...DEFAULT_AUTO_ORDER_PROGRESS_STEP };
 */
export const DEFAULT_AUTO_ORDER_PROGRESS_STEP: IAutoOrderOrderProgressStep = {
  /**
   * The order initialization has not started.
   */
  openAutoOrderOrderStarted: false,

  /**
   * The selected store has not been fetched.
   */
  getSelectedStore: false,

  /**
   * The selected order has not been committed to state.
   */
  setSelectedOrder: false,

  setSelectedSupplier: false,

  /**
   * Order items have not been fetched.
   */
  getOrderItems: false,

  getOrderItemsAdded: false,

  /**
   * Stock items for the regular supplier have not been loaded.
   */
  getStockItemsRegularSupplier: false,

  /**
   * Stock items for additional suppliers have not been loaded.
   */
  getStockItemsAdditionalSupplier: false,

  /**
   * Stock items have not been concatenated.
   */
  concatenateStockItems: false,

  /**
   * Duplicate stock items have not been filtered out.
   */
  deDuplicateStockItems: false,

  /**
   * Missing stock codes have not been determined.
   */
  determineMissingStockCodes: false,

  /**
   * Missing order item codes have not been detected.
   */
  determineMissingOrderItemCodes: false,

  /**
   * Previously edited stock items have not been retrieved from local storage.
   */
  getEditedStockItemsFromLocalStorage: false,

  /**
   * The auto order opening process has not completed.
   */
  openAutoOrderOrderCompleted: false,
};

//#endregion


//#region INTERFACE IOpenAutoGeneratedOrder

/**
 * ### IOpenAutoGeneratedOrder
 * Represents the full lifecycle progress of initializing or opening an auto-generated order.
 *
 * @interface IOpenAutoGeneratedOrder
 * @extends Object
 * @description Combines all relevant metadata, progress tracking, item grouping, and store context
 *              used during the creation or re-opening of an auto order. This structure supports
 *              detailed UI feedback and internal validation.
 *
 * @example
 * const orderProgress: IOpenAutoGeneratedOrder = {
 *   orderDetails: order,
 *   orderItems: [...],
 *   progress: 50,
 *   steps: { openAutoOrderOrderStarted: true, ... },
 *   stockItems: { duplicates: [], other: [], unique: [] },
 *   missing: {
 *     missingOrderItemCodes: [],
 *     missingStockCodes: [],
 *     orderItemsNewNotEqualAdded: [],
 *     validOrderItems: []
 *   },
 *   supplierStockItems: { main: [], additional: [] },
 *   storeDetails: store,
 *   localStorageEditedItems: [],
 *   totalSteps: 11
 * };
 */
export interface IOpenAutoGeneratedOrder extends Object {
  localStorageEditedItems: IAutoOrderOrderStockItem[];
  missing: IOpenAutoGeneratedOrderMissing;
  orderDetails: IAutoOrderOrder;
  orderItems: IAutoOrderOrderItem[];
  orderItemsAdded: IAutoOrderOrderItem[][];
  progress: number;
  steps: IAutoOrderOrderProgressStep;
  stockItems: {
    duplicates: IAutoOrderOrderStockItem[];
    other: IAutoOrderOrderStockItem[];
    unique: IAutoOrderOrderStockItem[];
  };
  storeDetails: IStore;
  success: boolean;
  supplierDetails: ISupplier;
  supplierStockItems: {
    added: IAutoOrderOrderStockItem[];
    additional: IAutoOrderOrderStockItem[];
    main: IAutoOrderOrderStockItem[];
  };
  totalSteps: number;
  localStorageStreams?: {
    onHand: {},
    quantity: {},
    price: {},
  };
}

//#endregion

//#region INTERFACE IOpenAutoGeneratedOrderMissing
export interface IOpenAutoGeneratedOrderMissing extends Object {
  orderItemMissing: IAutoOrderOrderStockItem[],
  stockItemMissingNewAdded: IAutoOrderOrderItem[],
  stockItemMissingNewFalse: IAutoOrderOrderItem[],
  stockItemMissingNewPeriod2: IAutoOrderOrderItem[],
  stockItemMissingNewTrue: IAutoOrderOrderItem[],
  validOrderItems: IAutoOrderOrderItem[],
  validOrderItemsStockItems: IAutoOrderOrderItem[],
  validStockItems: IAutoOrderOrderStockItem[],
  validStockItemsOrderItems: IAutoOrderOrderItem[],
}

//#endregion

//#region CONST DEFAULT_OPEN_AUTO_GENERATED_ORDER
/**
 * ### DEFAULT_OPEN_AUTO_GENERATED_ORDER
 * Default initialized object for `IOpenAutoGeneratedOrder` representing an empty or starting state.
 *
 * @constant DEFAULT_OPEN_AUTO_GENERATED_ORDER
 * @type {IOpenAutoGeneratedOrder}
 * @defaults {@link IOpenAutoGeneratedOrder}
 * @description This constant provides the base structure for a fresh or reset auto-generated order.
 *              It ensures all required fields of the `IOpenAutoGeneratedOrder` interface are populated
 *              with safe initial values such as empty arrays, zero progress, and default step flags.
 *              This is typically used during order initialization, recovery from local storage, or clearing state.
 *
 * @example
 * const newOrder: IOpenAutoGeneratedOrder = { ...DEFAULT_OPEN_AUTO_GENERATED_ORDER };
 */
export const DEFAULT_OPEN_AUTO_GENERATED_ORDER: IOpenAutoGeneratedOrder = {
  localStorageEditedItems: [],
  missing: {
    orderItemMissing: [],
    stockItemMissingNewAdded: [],
    stockItemMissingNewPeriod2: [],
    stockItemMissingNewTrue: [],
    stockItemMissingNewFalse: [],
    validOrderItems: [],
    validOrderItemsStockItems: [],
    validStockItems: [],
    validStockItemsOrderItems: [],
  },
  orderDetails: {} as IAutoOrderOrder,
  orderItems: [],
  orderItemsAdded: [],
  progress: 0,
  steps: {...DEFAULT_AUTO_ORDER_PROGRESS_STEP},
  stockItems: {
    duplicates: [],
    other: [],
    unique: [],
  },
  storeDetails: {...DEFAULT_STORE},
  success: undefined,
  supplierDetails: {} as ISupplier,
  supplierStockItems: {
    added: [],
    additional: [],
    main: [],
  },
  totalSteps: 0,
};

//#endregion
